/**
 * Chave de storage → URL de leitura assinada.
 *
 * O bucket é PRIVADO: foto de vistoria, assinatura e documento de cliente não
 * podem ter URL pública. O banco guarda a CHAVE; quem exibe pede aqui uma URL
 * assinada de curta duração — a API confere o prefixo do tenant e a permissão
 * da PASTA antes de assinar (`POST /arquivos/url-de-leitura`).
 *
 * O cache do TanStack Query segura a URL por menos tempo do que ela vale
 * (`staleTime` de 4 min para um TTL de assinatura ≥ 5 min), então uma imagem
 * re-renderizada não gasta uma assinatura nova — e nunca usa uma vencida.
 */

import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'

import { apiPost } from '@/lib/api'
import { ROTAS_DE_ARQUIVOS } from '@/lib/endpoints'

const leituraAssinadaSchema = z.object({
  urlDeLeitura: z.string(),
  expiraEmSegundos: z.number().int().positive(),
})

export type LeituraAssinada = z.infer<typeof leituraAssinadaSchema>

/** Menor que qualquer TTL de assinatura plausível — ver o cabeçalho. */
const STALE_TIME_MS = 4 * 60_000

function opcoesDeLeitura(chave: string) {
  return {
    queryKey: ['arquivos', 'url-de-leitura', chave] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiPost(ROTAS_DE_ARQUIVOS.urlDeLeitura, leituraAssinadaSchema, { chave }, { signal }),
    staleTime: STALE_TIME_MS,
    // Assinatura recusada (403/422) não melhora sozinha — repetir só polui.
    retry: false,
  }
}

/** URL de leitura de UMA chave. Passe `null` para não buscar nada. */
export function useUrlDeLeitura(chave: string | null): UseQueryResult<LeituraAssinada> {
  return useQuery({
    ...opcoesDeLeitura(chave ?? ''),
    enabled: chave !== null && chave !== '',
  })
}

/**
 * URLs de leitura de VÁRIAS chaves de uma vez (a grade de fotos da vistoria).
 * Devolve um mapa chave → URL só com as que já resolveram — a grade renderiza
 * o que tem e completa quando o resto chegar.
 */
export function useUrlsDeLeitura(chaves: readonly string[]): {
  urlPorChave: ReadonlyMap<string, string>
  carregando: boolean
} {
  const resultados = useQueries({
    queries: chaves.map((chave) => opcoesDeLeitura(chave)),
  })

  const urlPorChave = new Map<string, string>()
  for (const [indice, resultado] of resultados.entries()) {
    const chave = chaves[indice]
    if (chave !== undefined && resultado.data !== undefined) {
      urlPorChave.set(chave, resultado.data.urlDeLeitura)
    }
  }

  return {
    urlPorChave,
    carregando: resultados.some((resultado) => resultado.isPending),
  }
}
